import { EventType } from '@ag-ui/core';
import { v4 as uuid } from 'uuid';

import { AGUITransport } from 'modules/shared/libs/chat/transports/AGUITransport';

import type { AnswerThreadState } from '../api/answer-threads.api.types';
import type { AnswerFollowupAction } from '../utils/message-conversion.utils';
import type {
  BaseEvent,
  CustomEvent,
  Message as AGUIMessage,
} from '@ag-ui/core';
import type { ChatUIMessageChunk } from 'modules/shared/libs/chat';
import type { ChatUIMessage } from 'modules/shared/libs/chat/types';

type FollowupEvent = {
  message_id: string;
  followups: AnswerFollowupAction[];
  timestamp: string | null;
};

type AnswerTransportOptions = {
  api: string;
  credentials?: RequestCredentials;
  disabledSources?: string[];
};

export class AnswerTransport extends AGUITransport<ChatUIMessage> {
  private disabledSources?: string[];
  private threadState?: AnswerThreadState;
  private previewingVisibilityGroup?: number | null;

  constructor(options: AnswerTransportOptions) {
    super({
      api: options.api,
      credentials: options.credentials,
    });
    this.disabledSources = options.disabledSources;
  }

  setDisabledSources(disabledSources: string[] | undefined) {
    this.disabledSources = disabledSources;
  }

  setThreadState(state: AnswerThreadState | undefined) {
    this.threadState = state;
  }

  setPreviewingVisibilityGroup(id: number | null | undefined) {
    this.previewingVisibilityGroup = id;
  }

  protected override handleAGUIEvent(
    event: BaseEvent,
  ): ChatUIMessageChunk | ChatUIMessageChunk[] | null {
    if (event.type === EventType.CUSTOM) {
      const customEvent = event as CustomEvent;
      if (customEvent.name === 'FOLLOWUP') {
        const { followups: data } = customEvent.value as FollowupEvent;
        return {
          type: 'data-followups',
          id: this.currentRunId,
          data,
        };
      }
    }
    return super.handleAGUIEvent(event);
  }

  override sendMessages = async ({
    chatId,
    messages,
    abortSignal,
  }: {
    chatId: string;
    messages: ChatUIMessage[];
    abortSignal?: AbortSignal;
  }) => {
    const latestUserMessage = [...messages]
      .reverse()
      .find((msg) => msg.role === 'user');
    const messagesToSend = latestUserMessage ? [latestUserMessage] : [];

    // Assign UUIDs to messages without IDs (same as base AGUITransport)
    messagesToSend.forEach((msg) => {
      if (!msg.id) {
        msg.id = uuid();
      }
    });

    const aguiMessages = this.convertToAGUIMessages(messagesToSend);

    const requestBody: {
      thread_id: string;
      run_id: string;
      state: AnswerThreadState;
      messages: AGUIMessage[];
      disabled_sources?: string[];
    } = {
      thread_id: chatId,
      run_id: uuid(),
      state: this.threadState ?? {
        thread_type: 'CURRENT_COMPETE_ANSWER',
      },
      messages: aguiMessages,
      ...(this.disabledSources &&
        this.disabledSources.length > 0 && {
          disabled_sources: this.disabledSources,
        }),
    };

    // When a curator is previewing as a visibility group (general or
    // AskKlue-specific), propagate the group ID as a query param so the
    // BE returns the consumer toolset for the chat (mirrors
    // `checkCuratorAccess`). Sent as a query param rather than a custom
    // header to keep the request CORS-simple — traefik's preflight
    // allowlist doesn't cover custom X-Klue-* headers from the browser.
    const url =
      this.previewingVisibilityGroup != null
        ? `${this.api}${
            this.api.includes('?') ? '&' : '?'
          }previewing_visibility_group=${this.previewingVisibilityGroup}`
        : this.api;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify(requestBody),
      credentials: this.credentials,
      signal: abortSignal,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error('Response body is not readable');
    }

    return this.createAISDKStream(response.body);
  };
}
